import { Text } from '@/components/common';
import Button from '@/components/common/Button';
import { ImgWrapper } from '@/components/common/Loading/LoadingStyled';
import { ErrorMessage, PasswordField } from '@/components/form';
import { FormMarginDense } from '@/components/form/FormMarginDense';
import { FormWrapper } from '@/components/form/FormWrapper';
import CheckPassword from '@/components/page/Register/CheckPassword';
import { updateNewPassword, verifyPasswordResetCodeEmail } from '@/libs/firebase';
import { useAppDispatch } from '@/redux/hooks';
import { PasswordPolicySchema } from '@/utils/validation';
import { Box } from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { StyledForm } from './styled';
import { ModalRenderer } from '@/utils/modalUtils';
import ResetPassSuccessModal from '@/components/modal/ResetPassSuccess';

const schema = yup.object().shape({
  password: PasswordPolicySchema,
});

const ResetPasswordPage: React.FC<any> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isExpired, setIsExpired] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>('');

  useEffect(() => {
    const checkOob = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const oobCode = searchParams.get('oobCode');
      if (!oobCode) {
        setLoaded(true);
        setIsExpired(true);
        return;
      }
      try {
        await verifyPasswordResetCodeEmail(oobCode);
        setLoaded(true);
      } catch (error: any) {
        if (error.code === 'auth/invalid-action-code') {
          setLoaded(true);
          setIsExpired(true);
          return;
        }
      }
    };
    dispatch({ type: 'HIDE_LOADING' });
    checkOob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (values: any) => {
    dispatch({ type: 'SHOW_LOADING' });
    const searchParams = new URLSearchParams(window.location.search);
    const oobCode = searchParams.get('oobCode');
    if (!oobCode) return;
    try {
      await updateNewPassword(oobCode, values.password);
      ModalRenderer.open({
        component: ResetPassSuccessModal,
        theme: 'white',
        inMobile: 'bottom',
      });
    } catch (error: any) {
      setError(t('ERROR_MESSAGE_SOMETHING_WENT_WRONG'));
    } finally {
      dispatch({ type: 'HIDE_LOADING' });
    }
  };
  if (!loaded) {
    return (
      <Box sx={{ height: 500 }}>
        <ImgWrapper src="/images/logo/circle-logo-white.png" alt="Indicator" />
      </Box>
    );
  }

  return (
    <>
      <FormWrapper>
        <Text color="darkPrimary" variant="title">
          {t('RESET_MY_PASSWORD')}
        </Text>
        {isExpired ? (
          <Box sx={{ height: 400 }}>
            <Text color="darkSecondary" variant="content">
              Your request to reset your password has expired or the link has already been used.
            </Text>
          </Box>
        ) : (
          <Formik initialValues={{ password: '' }} validationSchema={schema} onSubmit={onSubmit}>
            {({ errors, values, isValid }) => (
              <StyledForm>
                <div>
                  {/* Phone number */}
                  <FormMarginDense>
                    <PasswordField
                      name="password"
                      config={{
                        variant: 'outlined',
                        label: t('NEW_PASSWORD'),
                        onClick: () => setError(null),
                        InputProps: {
                          'data-testid': 'test_password',
                        },
                      }}
                    />
                    <Box>
                      {/* @ts-ignore */}
                      <CheckPassword newPassword={values?.password} />
                    </Box>
                  </FormMarginDense>
                </div>
                {error ? <ErrorMessage>{error}</ErrorMessage> : null}
                <FormMarginDense>
                  <Button
                    fullWidth
                    variant="contained"
                    color="white"
                    type="submit"
                    disabled={!(isValid && values.password)}
                    data-testid="test_btn_continue"
                  >
                    {t('CONTINUE')}
                  </Button>
                </FormMarginDense>
              </StyledForm>
            )}
          </Formik>
        )}
      </FormWrapper>
      {/* <RecapchaContainer ref={reCapchaContainerElement}>
        <div id="recapcha-container" />
      </RecapchaContainer> */}
    </>
  );
};

export default ResetPasswordPage;
